import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ILocation } from '../../type/location';
import useLocationStore from '../../store/locatonStore';
import operationAPI from '../../api/operationApi';
import DateSelector from '../../components/common/calendar/dateSelector';
import SunIcon from '../../assets/SunIcon';
import { encodeDateForUrl, getAdjustedDate } from '../../utils/dateUtils';
import ReusableButton from '../../components/common/Button/Button';
import EditIcon from '../../assets/EditIcon';
import AccordianDownIcon from '../../assets/AccordianDownIcon';
import ReusableIconButton from '../../components/common/Button/ReusableIconButton';
import CrossIcon from '../../assets/CrossIcon';
import CheckIcon from '../../assets/CheckIcon';
import flightApi from '../../api/flightAllocation';
import UnallocatedCard from '../../components/common/UnallocatedCard';
import WarningIcon from '../../assets/WarningIcon';
import FlightCard from '../../components/common/FlightCard';
import { IFlightOperationDetails } from '../../interfaces/flightOperationDetails';
import { IFlightBookingDetails } from '../../interfaces/flightBookingDetails';
import {
  calculatePaxPercentage,
  calculateTotalWeight,
  showOrReplaceToast,
} from '../../utils/utility';
import Loader from '../../assets/loader/Loader';
import {
  flightHeader,
  flightHeaderKeyMapping as keyMapping,
} from '../../constants/headerConstants';
import { translate } from '../../i18n';
import {
  allocation,
  CUSTOM_TOAST_ID,
  defaultHiddenFlightColumns,
  flightHeaderToolTip,
  flightHeaderToolTipColumns,
  truncateColumn,
} from '../../constants/constant';
import { IVisibleColumns } from '../../interfaces/visibleColumns';
import { ITableHeader } from '../../interfaces/tableHeader';
import useTemperatureSecondStore from '../../store/temperatureSecondStore';
import useDateStore from '../../store/selectedDateStore';
import { IOriginalAllocation } from '../../interfaces/originalFlightAllocation';
import { ISelectedFlightsData } from '../../interfaces/selectedFlightsData';
import useBgColorStore from '../../store/bgColor';
import CalendarHeader from '../../components/common/Header/CalenderHeader';
import SunIconDark from '../../assets/SunIconDark';
import EditIconDark from '../../assets/EditIconDark';
import AccordianDownIconDark from '../../assets/AccordianDownIconDark';
import { useClickOutside } from '../../hooks/useClickOutside';
import ReusableSelectLocation from '../../components/common/SelectLocation';
import ReusableSelect from '../../components/common/Select';
import { ITemperature } from '../../type/temperature';
import useTemperatureStore from '../../store/temperatureStore';
import { IOperations } from '../../type/operation';
import { IAddTemperatureRequest } from '../../type/addTemp';
import {
  useOperationDetails,
  useTemperatureList,
  useTodayTemperature,
} from '../../hooks/apiHook/operationDetails';

//TODO: need to recheck this logic here

const FlightDesktop = () => {
  const { selectedDateFromStore, setSelectedDateFromStore } = useDateStore();
  const [headers, setHeaders] = useState<ITableHeader[]>(flightHeader);
  const { selectedTemperature2 } = useTemperatureSecondStore();
  const defaultVisibleColumns = headers?.reduce<IVisibleColumns>(
    (acc, header) => {
      const key = header?.label?.replace(/\s+/g, '')?.toLowerCase();
      acc[key] = !defaultHiddenFlightColumns.includes(key);
      return acc;
    },
    {},
  );

  const [visibleColumns, setVisibleColumns] = useState<IVisibleColumns>(
    defaultVisibleColumns,
  );

  const [locationList, setLocationList] = useState<ILocation[]>();
  const [isUnallocatedVisible, setIsUnallocatedVisible] = useState(false);
  const [flightData, setFlightData] = useState<{
    flightList: IFlightOperationDetails[];
    bookings: IFlightBookingDetails[];
    totalPax: number;
    totalAllocatedPax: number;
    totalUnAllocatedPax: number;
  }>({
    flightList: [],
    bookings: [],
    totalPax: 0,
    totalAllocatedPax: 0,
    totalUnAllocatedPax: 0,
  });

  const [unAllocatedPax, setUnAllocatedPax] = useState<any>(null);
  const [unAllocatedPaxPercentage, setUnAllocatedPaxPercentage] =
    useState<String>('0');

  const [loading, setLoading] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [isToggleColumns, setIsToggleColumns] = useState<boolean>(false);

  const [selectedFlight, setSelectedFlight] = useState<{
    [key: string]: ISelectedFlightsData;
  }>({});

  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date>(
    selectedDateFromStore || getAdjustedDate(),
  );
    useEffect(() => {
    setSelectedDate(selectedDateFromStore);
  }, [selectedDateFromStore]);

  const [originalAllocations, setOriginalAllocations] = useState<{
    [bookingId: string]: IOriginalAllocation[];
  }>({});
  const { bgColor, updateBgColor } = useBgColorStore();
  const selectedLocation = useLocationStore((state) => state.selectedLocation);
  const [selectedLocationId, setSelectedLocationId] = useState<string | number>(
    selectedLocation?.OwnerId,
  );

  const [realTimeFlightPax, setRealTimeFlightPax] = useState<{
    [key: string]: number;
  }>({
    totalPax: 0,
    totalAllocatedPax: 0,
  });
  const [flightAllocations, setFlightAllocations] = useState<{
    [operationBalloonFlightId: string]: number;
  }>({});

  const [busAllocationsWeight, setBusAllocationsWeight] = useState<{
    [operationBalloonFlightId: string]: number;
  }>({});

  const setSelectedLocation = useLocationStore(
    (state) => state.setSelectedLocation,
  );
  const handleLocationChange = (selectedLocation: ILocation) => {
    setSelectedLocationId(selectedLocation.OwnerId);
    setSelectedLocation(selectedLocation);
    updateBgColor();
  };

  const fetchOwnerLocations = useCallback(async () => {
    try {
      const response = await operationAPI.getLocationList();
      setLocationList(response?.data);
    } catch (error) {
      console.log(error);
    }
  }, []);
  useEffect(() => {
    fetchOwnerLocations();
  }, [fetchOwnerLocations]);

  const fetchFlightAllocationDetails = useCallback(
    async (date: Date, ownerId: number, isShowLoader?: boolean) => {
      const encodedDate = encodeDateForUrl(date);

      try {
        setIsPageLoading(isShowLoader ?? false);
        const response = await flightApi.getFlightAllocationDetails(
          ownerId,
          encodedDate,
        );
        setFlightData(response?.data);
        const percentageNeeded = calculatePaxPercentage(
          response?.data?.totalPax,
          response?.data?.totalAllocatedPax,
        );
        setUnAllocatedPaxPercentage(percentageNeeded?.toFixed(0));
        // Initialize selectedBuses and originalAllocations
        const initialSelectedBuses: { [key: string]: ISelectedFlightsData } =
          {};
        const initialOriginalAllocations: {
          [key: string]: IOriginalAllocation[];
        } = {};

        response?.data?.bookings?.forEach((booking: IFlightBookingDetails) => {
          const bookingIdStr = String(booking.bookingId);
          const zoneIdStr = String(booking.zoneId);
          const bookingProductTripIdStr = String(booking.bookingProductTripId);
          const combinedKey = `${bookingIdStr}_${zoneIdStr}_${bookingProductTripIdStr}`;

          // Initialize arrays if needed
          if (!initialOriginalAllocations[combinedKey]) {
            initialOriginalAllocations[combinedKey] = [];
          }

          if (!initialSelectedBuses[combinedKey]) {
            initialSelectedBuses[combinedKey] = {
              bookingProductTripId: booking.bookingProductTripId,
              bookingId: booking.bookingId,
              paxNo: booking.paxNo,
              allocNo: booking?.allocNo,
              zoneId: booking?.zoneId,
              allocations: [],
            };
          }

          // Handle array of allocations
          booking.flightAllocation?.forEach((allocation) => {
            if (allocation.operationBalloonFlightId) {
              // Add to original allocations
              initialOriginalAllocations[combinedKey].push({
                operationBalloonFlightId: allocation.operationBalloonFlightId,
                paxNo: Number(allocation.paxNo || booking.paxNo),
              });

              // Add to selected buses
              initialSelectedBuses[combinedKey].allocations.push({
                operationBalloonFlightId: allocation.operationBalloonFlightId,
                splitMaleNo: Number(allocation?.male),
                splitFemaleNo: Number(allocation?.female),
                splitChildNo: Number(allocation?.child),
                oldOperationBalloonFlightId:
                  allocation.operationBalloonFlightId,
                splitPax: Number(allocation.paxNo || booking.paxNo),
                isDeleted: false,
                maleWeight: booking?.maleWeight,
                femaleWeight: booking?.femaleWeight,
                childWeight: booking?.childWeight,
              });
            }
          });

          // If no allocations were found, remove the entry
          if (initialSelectedBuses[combinedKey].allocations.length === 0) {
            delete initialSelectedBuses[combinedKey];
          }
        });
        // set value
        setOriginalAllocations(initialOriginalAllocations);
        setSelectedFlight(initialSelectedBuses);
      } catch (error) {
      } finally {
        setIsPageLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    fetchFlightAllocationDetails(selectedDate, selectedLocation.OwnerId, true);
  }, [selectedDate, fetchFlightAllocationDetails, selectedLocation]);

  const onIsUnallocatedVisible = async () => {
    await fetchFlightUnallocatedPax(selectedDate);
    setIsUnallocatedVisible(true);
  };

  const fetchFlightUnallocatedPax = async (date: Date) => {
    try {
      const encodedDate = encodeDateForUrl(date);
      const response = await flightApi.getFlightUnAllocatedPaxDetails(
        selectedLocation.OwnerId,
        encodedDate,
      );
      setUnAllocatedPax(response?.data);
    } catch (error) {
    } finally {
      // setLoading(false);
    }
  };

  // save or update allocated flight data
  const allocatedFlight = async () => {
    try {
      if (Object?.values(selectedFlight)?.length === 0) {
        showOrReplaceToast(
          CUSTOM_TOAST_ID.flight,
          translate('flightAllocation.errorMessage.FlightAllocation'),
          'error',
        );
        return;
      }

      const payload = Object?.entries(selectedFlight)?.flatMap(
        ([bookingId, booking]) => {
          const original = originalAllocations[bookingId] || [];
          const current = booking.allocations;

          return current
            .filter((allocation) => {
              const originalAlloc = original.find(
                (orig) =>
                  String(orig.operationBalloonFlightId) ===
                  String(allocation.operationBalloonFlightId),
              );
              return (
                allocation.isDeleted ||
                !originalAlloc ||
                originalAlloc.paxNo !== allocation.splitPax
              );
            }) // Filter out allocations with splitPax that is undefined, null, or 0
            .filter((allocation) => {
              // Allow deleted allocations through regardless of paxNo
              if (allocation.isDeleted) return true;

              // Filter out allocations with invalid pax counts
              return (
                (allocation.splitMaleNo !== undefined &&
                  allocation.splitMaleNo !== null &&
                  allocation.splitMaleNo > 0) ||
                (allocation.splitFemaleNo !== undefined &&
                  allocation.splitFemaleNo !== null &&
                  allocation.splitFemaleNo > 0) ||
                (allocation.splitChildNo !== undefined &&
                  allocation.splitChildNo !== null &&
                  allocation.splitChildNo > 0)
              );
            })
            .map((allocation) => {
              const bookingData = {
                maleWeight:
                  allocation.splitMaleNo * Number(allocation.maleWeight || 0),
                male: allocation.splitMaleNo,
                femaleWeight:
                  allocation.splitFemaleNo *
                  Number(allocation.femaleWeight || 0),
                female: allocation.splitFemaleNo,
                childWeight:
                  allocation.splitChildNo * Number(allocation.childWeight || 0),
                child: allocation.splitChildNo,
              };

              const totalWeight = calculateTotalWeight(bookingData);

              return {
                bookingProductTripId: booking.bookingProductTripId,
                bookingId: Number(booking.bookingId),
                operationBalloonFlightId: allocation.operationBalloonFlightId,
                paxNo:
                  allocation.splitMaleNo +
                  allocation.splitFemaleNo +
                  allocation.splitChildNo,

                maleNo: allocation.splitMaleNo | 0,
                femaleNo: allocation.splitFemaleNo | 0,
                childNo: allocation.splitChildNo | 0,

                weight: totalWeight,

                ...(allocation.oldOperationBalloonFlightId && {
                  oldOperationBalloonFlightId:
                    allocation.oldOperationBalloonFlightId,
                }),

                isDeleted: allocation.isDeleted || false,
              };
            });
        },
      );

      if (payload.length > 0) {
        console.log('this is flight allocation payload here ', payload); //TODO: I will remove this in next PR
        setLoading(true);
        await flightApi.addFlightPickupAllocation(payload);
        await fetchFlightAllocationDetails(
          selectedDate,
          selectedLocation.OwnerId,
        );
        setIsEditMode(false);
        showOrReplaceToast(
          'flight',
          translate('flightAllocation.successMessage.allocationSave'),
          'success',
        );
      } else {
        console.error('No changes detected in allocations'); //TODO: I will remove this in next PR
      }
    } catch (error) {
      console.error('Error fetching allocation details:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (
      flightData &&
      flightData?.flightList &&
      flightData?.flightList?.length > 0
    ) {
      const flightHeaders = flightData?.flightList?.map((flight) => ({
        label: `${flight.balloonName}`,
        key: flight.operationBalloonFlightId?.toString(),
      }));

      const staticHeaders = headers?.filter((header) => !header.key) || [];
      const newHeaders = [...staticHeaders, ...flightHeaders];

      setHeaders(newHeaders);

      setVisibleColumns((prevVisibleColumns) => {
        const updatedVisibleColumns: Record<string, boolean> = {};

        // Add all static header visibility
        staticHeaders.forEach((header) => {
          const key = header.label?.replace(/\s+/g, '')?.toLowerCase();
          updatedVisibleColumns[key] =
            prevVisibleColumns[key] !== undefined
              ? prevVisibleColumns[key]
              : !defaultHiddenFlightColumns.includes(key);
        });

        // Add new flight header visibility - preserve existing state or default to true
        flightHeaders?.forEach((flightHeader) => {
          const key = flightHeader.label?.replace(/\s+/g, '')?.toLowerCase();
          updatedVisibleColumns[key] = true;
        });

        return updatedVisibleColumns;
      });

      flightHeaders?.forEach((flight) => {
        keyMapping[flight.key] = flight.key;
      });
    } else {
      setHeaders((prevHeaders) => prevHeaders?.filter((header) => !header.key));
      // Remove flight columns from visibleColumns when no flights
      setVisibleColumns((prev) => {
        const updated = { ...prev };
        Object.keys(updated).forEach((key) => {
          // Check if this key corresponds to a flight column by checking if it matches any static header
          const isStaticColumn = flightHeader.some(
            (header) =>
              header.label?.replace(/\s+/g, '')?.toLowerCase() === key,
          );
          if (!isStaticColumn) {
            delete updated[key];
          }
        });
        return updated;
      });
    }
  }, [flightData]);

  useEffect(() => {
    if (flightData) {
      setRealTimeFlightPax({
        totalPax: flightData?.totalPax ?? 0,
        totalAllocatedPax: flightData?.totalAllocatedPax ?? 0,
      });

      // Initialize bus-specific allocation counts
      const initialBusAllocs: { [operationBalloonFlightId: string]: number } =
        {};
      const initialBusAllocsWeight: {
        [operationBalloonFlightId: string]: number;
      } = {};

      flightData?.flightList?.forEach((flight) => {
        initialBusAllocs[flight.operationBalloonFlightId] =
          flight.allocatedPax || 0;

        initialBusAllocsWeight[flight.operationBalloonFlightId] =
          flight.totalWeight || 0;
      });
      setFlightAllocations(initialBusAllocs);
      setBusAllocationsWeight(initialBusAllocsWeight);
    }
  }, [flightData]);

  //calculate and update allocation stats based on changes
  const updateAllocationStats = useCallback(() => {
    // Calculate bus-specific allocations
    const newBusAllocations: { [operationBalloonFlightId: string]: number } =
      {};

    const newBusAllocationsWeight: {
      [operationBalloonFlightId: string]: number;
    } = {};
    flightData?.flightList?.forEach((flight) => {
      newBusAllocations[flight.operationBalloonFlightId] = 0;
      newBusAllocationsWeight[flight.operationBalloonFlightId] = 0;
    });

    // Calculate total allocated pax and bus-specific allocations
    let totalAllocatedPax = 0;

    Object.values(selectedFlight).forEach((booking) => {
      booking?.allocations?.forEach((allocation) => {
        if (!allocation.isDeleted) {
          const busId = allocation.operationBalloonFlightId.toString();
          // const paxCount = allocation.splitPax;
          const maleNo = allocation.splitMaleNo
            ? Number(allocation.splitMaleNo)
            : 0;
          const femaleNo = allocation.splitFemaleNo
            ? Number(allocation.splitFemaleNo)
            : 0;
          const childNo = allocation.splitChildNo
            ? Number(allocation.splitChildNo)
            : 0;

          const paxCount = maleNo + femaleNo + childNo;
          const calculateWeightObj = {
            maleWeight: allocation?.maleWeight,
            male: maleNo,
            femaleWeight: allocation?.femaleWeight,
            female: femaleNo,
            childWeight: allocation?.childWeight,
            child: childNo,
          };
          const totalWeight = calculateTotalWeight(calculateWeightObj);

          if (newBusAllocations[busId] !== undefined) {
            newBusAllocations[busId] += paxCount;
            newBusAllocationsWeight[busId] += totalWeight;
            totalAllocatedPax += paxCount;
          }
        }
      });
    });

    // Include original allocations that are not in selectedFlight
    flightData?.bookings?.forEach((booking) => {
      // Skip if this booking is in our selectedFlight (already counted)
      if (selectedFlight[`${booking?.bookingId}_${booking?.zoneId}_${booking?.bookingProductTripId}`]) return;

      // Add all original allocations from bookings not in selectedBuses
      booking.flightAllocation?.forEach((allocation) => {
        if (allocation.operationBalloonFlightId) {
          const busId = allocation.operationBalloonFlightId.toString();
          const maleCount = Number(allocation.male ?? booking.male);
          const femaleCount = Number(allocation.female ?? booking.female);
          const childCount = Number(allocation.child ?? booking.child);
          const paxCount = maleCount + femaleCount + childCount;

          const calculateWeightObj = {
            maleWeight: booking?.maleWeight,
            male: maleCount,
            femaleWeight: booking?.femaleWeight,
            female: femaleCount,
            childWeight: booking?.childWeight,
            child: childCount,
          };
          const totalWeight = calculateTotalWeight(calculateWeightObj);

          if (newBusAllocations[busId] !== undefined) {
            newBusAllocations[busId] += paxCount;
            newBusAllocationsWeight[busId] += totalWeight;
            totalAllocatedPax += paxCount;
          }
        }
      });
    });

    // Update the state with new allocations
    setFlightAllocations(newBusAllocations);
    setBusAllocationsWeight(newBusAllocationsWeight);

    setRealTimeFlightPax((prev) => ({
      ...prev,
      totalAllocatedPax,
    }));
  }, [selectedFlight, flightData]);

  // Call this function whenever selectedBuses changes
  useEffect(() => {
    if (isEditMode) {
      updateAllocationStats();
    }
  }, [selectedFlight, isEditMode, updateAllocationStats]);

  const handleDateChange = (date: Date | null) => {
    if (date) {
      setSelectedDate(date);
      setSelectedDateFromStore(date);
    }
  };
  const onSetIsToggleColumns = () => {
    setIsToggleColumns(!isToggleColumns);
  };

  const ref = useClickOutside<HTMLDivElement>(() => {
    setIsToggleColumns(false);
  });

  // TODO: just last updated code and handle allocated pax unselect issue
  const handleCheckboxChange = (
    bookingId: number,
    operationBalloonFlightId: number,
    bookingProductTripId: number,
    paxNo: number,
    maleNo: number,
    femaleNo: number,
    childNo: number,
    maleWeight: string | number,
    femaleWeight: string | number,
    childWeight: string | number,
    zoneId: number,
    oldOperationBalloonFlightId?: number | null,
  ) => {
    const bookingIdString = String(bookingId);
    const operationBalloonFlightIdStr = String(operationBalloonFlightId);
    const zoneIdStr = String(zoneId);
    const bookingProductTripIdStr = String(bookingProductTripId);
    const combinedKey = `${bookingIdString}_${zoneIdStr}_${bookingProductTripIdStr}`;

    setSelectedFlight((prev) => {
      const currentBooking = prev[combinedKey];
      // Check if this is an original allocation
      const isOriginalAllocation = originalAllocations[combinedKey]?.some(
        (orig) =>
          String(orig.operationBalloonFlightId) === operationBalloonFlightIdStr,
      );

      if (!currentBooking) {
        // First selection for this booking
        return {
          ...prev,
          [combinedKey]: {
            bookingProductTripId,
            bookingId,
            paxNo,
            allocNo: paxNo,
            zoneId,
            allocations: [
              {
                operationBalloonFlightId,
                oldOperationBalloonFlightId,
                splitPax: Number(paxNo),
                splitMaleNo: Number(maleNo),
                splitFemaleNo: Number(femaleNo),
                splitChildNo: Number(childNo),
                maleWeight: maleWeight || 0,
                femaleWeight: femaleWeight || 0,
                childWeight: childWeight || 0,
                isDeleted: false,
              },
            ],
          },
        } as { [key: string]: ISelectedFlightsData };
      }

      // Check if this flight is already in allocations (active or deleted)
      const existingAllocationIndex = currentBooking.allocations.findIndex(
        (a) =>
          String(a.operationBalloonFlightId) === operationBalloonFlightIdStr,
      );

      if (
        existingAllocationIndex !== -1 &&
        currentBooking.allocations[existingAllocationIndex].isDeleted
      ) {
        // This is a previously deleted allocation that should be reactivated
        const updatedAllocations = [...currentBooking.allocations];
        const activeAllocations = updatedAllocations.filter(
          (a, idx) => idx !== existingAllocationIndex && !a.isDeleted,
        );

        // If there are no other active allocations, give this one the full pax count
        const splitPax = activeAllocations.length === 0 ? Number(paxNo) : 0;
        const splitMaleNo = activeAllocations.length === 0 ? Number(maleNo) : 0;
        const splitFemaleNo =
          activeAllocations.length === 0 ? Number(femaleNo) : 0;
        const splitChildNo =
          activeAllocations.length === 0 ? Number(childNo) : 0;

        updatedAllocations[existingAllocationIndex] = {
          ...updatedAllocations[existingAllocationIndex],
          isDeleted: false,
          splitPax,
          splitMaleNo,
          splitFemaleNo,
          splitChildNo,
        };

        const totalAllocated = updatedAllocations.reduce((sum, alloc) => {
          if (alloc.isDeleted) return sum;
          const male = Number(alloc.splitMaleNo) || 0;
          const female = Number(alloc.splitFemaleNo) || 0;
          const child = Number(alloc.splitChildNo) || 0;
          return sum + male + female + child;
        }, 0);

        return {
          ...prev,
          [combinedKey]: {
            ...currentBooking,
            allocations: updatedAllocations,
            allocNo: totalAllocated,
          },
        };
      }

      // Normal checkbox handling for toggling a currently active allocation
      if (
        existingAllocationIndex !== -1 &&
        !currentBooking.allocations[existingAllocationIndex].isDeleted
      ) {
        // Bus is already selected - handle unselection
        if (isOriginalAllocation) {
          // For originally allocated buses, mark for deletion instead of removing
          const updatedAllocations = currentBooking.allocations.map(
            (alloc, index) =>
              index === existingAllocationIndex
                ? { ...alloc, isDeleted: true }
                : alloc,
          );

          // Get the counts from the allocation being deleted
          const paxToRedistribute =
            updatedAllocations[existingAllocationIndex].splitPax;
          const maleToRedistribute =
            updatedAllocations[existingAllocationIndex].splitMaleNo;
          const femaleToRedistribute =
            updatedAllocations[existingAllocationIndex].splitFemaleNo;
          const childToRedistribute =
            updatedAllocations[existingAllocationIndex].splitChildNo;

          // Find active allocations (not marked for deletion)
          const activeAllocations = updatedAllocations.filter(
            (a) => !a.isDeleted,
          );

          // Redistribute pax counts for active allocations
          if (activeAllocations.length > 0) {
            if (activeAllocations.length === 1) {
              // If only one active allocation remains, give it all passengers
              const allocIndex = updatedAllocations.findIndex(
                (a) => a === activeAllocations[0],
              );
              if (allocIndex !== -1) {
                updatedAllocations[allocIndex] = {
                  ...updatedAllocations[allocIndex],
                  splitPax: Number(paxNo),
                  splitMaleNo: Number(maleNo),
                  splitFemaleNo: Number(femaleNo),
                  splitChildNo: Number(childNo),
                };
              }
            } else {
              // Calculate distribution for multiple allocations
              const paxPerAllocation = Math.floor(
                paxToRedistribute / activeAllocations.length,
              );
              const malePerAllocation = Math.floor(
                maleToRedistribute / activeAllocations.length,
              );
              const femalePerAllocation = Math.floor(
                femaleToRedistribute / activeAllocations.length,
              );
              const childPerAllocation = Math.floor(
                childToRedistribute / activeAllocations.length,
              );

              const paxRemainder = paxToRedistribute % activeAllocations.length;
              const maleRemainder =
                maleToRedistribute % activeAllocations.length;
              const femaleRemainder =
                femaleToRedistribute % activeAllocations.length;
              const childRemainder =
                childToRedistribute % activeAllocations.length;

              // Reset the counts for all active allocations
              activeAllocations.forEach((alloc, idx) => {
                const allocIndex = updatedAllocations.findIndex(
                  (a) => a === alloc,
                );
                if (allocIndex !== -1) {
                  updatedAllocations[allocIndex] = {
                    ...updatedAllocations[allocIndex],
                    splitPax:
                      updatedAllocations[allocIndex].splitPax +
                      paxPerAllocation +
                      (idx === 0 ? paxRemainder : 0),
                    splitMaleNo:
                      updatedAllocations[allocIndex].splitMaleNo +
                      malePerAllocation +
                      (idx === 0 ? maleRemainder : 0),
                    splitFemaleNo:
                      updatedAllocations[allocIndex].splitFemaleNo +
                      femalePerAllocation +
                      (idx === 0 ? femaleRemainder : 0),
                    splitChildNo:
                      updatedAllocations[allocIndex].splitChildNo +
                      childPerAllocation +
                      (idx === 0 ? childRemainder : 0),
                  };
                }
              });
            }
          }

          // Calculate total allocated pax for the booking
          const totalAllocatedPax = updatedAllocations.reduce((sum, alloc) => {
            if (alloc.isDeleted) return sum;
            return sum + alloc.splitPax;
          }, 0);

          return {
            ...prev,
            [combinedKey]: {
              ...currentBooking,
              allocations: updatedAllocations,
              allocNo: totalAllocatedPax,
            },
          };
        } else {
          // For new allocations, remove them entirely
          const paxToRedistribute =
            currentBooking.allocations[existingAllocationIndex].splitPax;
          const newAllocations = currentBooking.allocations.filter(
            (_, index) => index !== existingAllocationIndex,
          );

          if (newAllocations.length === 0) {
            const newSelected = { ...prev };
            delete newSelected[combinedKey];
            return newSelected;
          }

          // Redistribute pax among remaining allocations
          if (newAllocations.length === 1) {
            // When only one flight remains selected, allocate ALL passengers to it
            newAllocations[0].splitPax = Number(paxNo);
            newAllocations[0].splitMaleNo = Number(maleNo);
            newAllocations[0].splitFemaleNo = Number(femaleNo);
            newAllocations[0].splitChildNo = Number(childNo);
          } else if (newAllocations.length > 1) {
            // For multiple flights, distribute passengers proportionally
            const paxPerAllocation = Math.floor(
              paxToRedistribute / newAllocations.length,
            );
            const remainder = paxToRedistribute % newAllocations.length;

            // Also distribute gender-specific counts proportionally
            const malePerAllocation = Math.floor(
              Number(maleNo) / newAllocations.length,
            );
            const femalePerAllocation = Math.floor(
              Number(femaleNo) / newAllocations.length,
            );
            const childPerAllocation = Math.floor(
              Number(childNo) / newAllocations.length,
            );

            const maleRemainder = Number(maleNo) % newAllocations.length;
            const femaleRemainder = Number(femaleNo) % newAllocations.length;
            const childRemainder = Number(childNo) % newAllocations.length;

            newAllocations.forEach((alloc, idx) => {
              alloc.splitPax += paxPerAllocation + (idx === 0 ? remainder : 0);
              alloc.splitMaleNo =
                malePerAllocation + (idx === 0 ? maleRemainder : 0);
              alloc.splitFemaleNo =
                femalePerAllocation + (idx === 0 ? femaleRemainder : 0);
              alloc.splitChildNo =
                childPerAllocation + (idx === 0 ? childRemainder : 0);
            });
          }

          // Calculate total allocated pax for the booking
          const totalAllocatedPax = newAllocations.reduce((sum, alloc) => {
            return sum + alloc.splitPax;
          }, 0);

          return {
            ...prev,
            [combinedKey]: {
              ...currentBooking,
              allocations: newAllocations,
              allocNo: totalAllocatedPax,
            },
          };
        }
      }

      // Add new balloon flight allocation
      const newAllocation = {
        operationBalloonFlightId,
        oldOperationBalloonFlightId: null, // Preserve the oldOperationBalloonFlightId if it exists
        splitPax: 0,
        splitMaleNo: 0,
        splitFemaleNo: 0,
        splitChildNo: 0,
        maleWeight: maleWeight || 0,
        femaleWeight: femaleWeight || 0,
        childWeight: childWeight || 0,
        isDeleted: false,
      };

      // Filter out the deleted allocations for calculation purposes
      const activeAllocations = currentBooking.allocations.filter(
        (a) => !a.isDeleted,
      );
      const newAllocations = [...currentBooking.allocations, newAllocation];

      // Calculate split pax
      if (activeAllocations.length === 0) {
        // If all previous allocations were deleted, give full pax to new allocation
        newAllocation.splitPax = Number(paxNo);
        newAllocation.splitMaleNo = Number(maleNo);
        newAllocation.splitFemaleNo = Number(femaleNo);
        newAllocation.splitChildNo = Number(childNo);
      } else {
        // Calculate the total pax already allocated to active allocations
        const allocatedPax = activeAllocations.reduce(
          (sum, alloc) => sum + alloc.splitPax,
          0,
        );
        const allocatedMales = activeAllocations.reduce(
          (sum, alloc) => sum + alloc.splitMaleNo,
          0,
        );
        const allocatedFemales = activeAllocations.reduce(
          (sum, alloc) => sum + alloc.splitFemaleNo,
          0,
        );
        const allocatedChildren = activeAllocations.reduce(
          (sum, alloc) => sum + alloc.splitChildNo,
          0,
        );

        // Available pax = total pax - allocated pax
        const availablePax = Number(paxNo) - allocatedPax;
        const availableMales = Number(maleNo) - allocatedMales;
        const availableFemales = Number(femaleNo) - allocatedFemales;
        const availableChildren = Number(childNo) - allocatedChildren;

        // Assign available pax to the new allocation
        newAllocation.splitPax = Math.max(0, availablePax);
        newAllocation.splitMaleNo = Math.max(0, availableMales);
        newAllocation.splitFemaleNo = Math.max(0, availableFemales);
        newAllocation.splitChildNo = Math.max(0, availableChildren);
      }

      return {
        ...prev,
        [combinedKey]: {
          ...currentBooking,
          allocations: newAllocations,
        },
      };
    });
  };

  // update  the toggleEditMode function to reset values when canceling edits
  const toggleEditMode = () => {
    // if (isEditMode) {
    const originalBusAllocs: { [operationBalloonFlightId: string]: number } =
      {};
    const originalBusAllocsWeight: {
      [operationBalloonFlightId: string]: number;
    } = {};

    flightData?.flightList?.forEach((flight) => {
      originalBusAllocs[flight.operationBalloonFlightId] =
        flight.allocatedPax || 0;
      originalBusAllocsWeight[flight.operationBalloonFlightId] =
        flight.totalWeight || 0;
    });
    setFlightAllocations(originalBusAllocs);
    setBusAllocationsWeight(originalBusAllocsWeight);

    // Restore original busPax count
    setRealTimeFlightPax({
      totalPax: flightData?.totalPax ?? 0,
      totalAllocatedPax: flightData?.totalAllocatedPax ?? 0,
    });

    // Reset selectedFlight to original state
    const initialSelectedBuses: { [key: string]: ISelectedFlightsData } = {};
    const initialOriginalAllocations: {
      [key: string]: IOriginalAllocation[];
    } = {};

    flightData?.bookings?.forEach((booking) => {
      const bookingIdString = String(booking.bookingId);
      const zoneIdStr = String(booking?.zoneId);
      const bookingProductTripIdStr = String(booking?.bookingProductTripId);
      const combinedKey = `${bookingIdString}_${zoneIdStr}_${bookingProductTripIdStr}`;

      if (!initialOriginalAllocations[combinedKey]) {
        initialOriginalAllocations[combinedKey] = [];
      }

      if (!initialSelectedBuses[combinedKey]) {
        initialSelectedBuses[combinedKey] = {
          bookingProductTripId: booking.bookingProductTripId,
          bookingId: booking.bookingId,
          paxNo: booking.paxNo,
          allocNo: booking.allocNo,
          zoneId: booking.zoneId,
          allocations: [],
        };
      }

      booking.flightAllocation?.forEach((allocation) => {
        if (allocation.operationBalloonFlightId) {
          initialOriginalAllocations[combinedKey].push({
            operationBalloonFlightId: allocation.operationBalloonFlightId,
            paxNo: Number(allocation.paxNo || booking.paxNo),
          });

          initialSelectedBuses[combinedKey].allocations.push({
            operationBalloonFlightId: allocation.operationBalloonFlightId,
            splitMaleNo: Number(allocation?.male),
            splitFemaleNo: Number(allocation?.female),
            splitChildNo: Number(allocation?.child),
            oldOperationBalloonFlightId: allocation.operationBalloonFlightId,
            splitPax: Number(allocation.paxNo || booking.paxNo),
            isDeleted: false,
            maleWeight: booking?.maleWeight,
            femaleWeight: booking?.femaleWeight,
            childWeight: booking?.childWeight,
          });
        }
      });
      if (initialSelectedBuses[combinedKey].allocations.length === 0) {
        delete initialSelectedBuses[combinedKey];
      }
    });

    setOriginalAllocations(initialOriginalAllocations);
    setSelectedFlight(initialSelectedBuses);
    // }
    setIsEditMode((prev) => !prev);
  };

  // Handle cancel button click
  const handleCancelEdit = () => {
    // Reset to original state of allocations
    const originalBusAllocs: { [operationBalloonFlightId: string]: number } =
      {};

    const originalBusAllocsWeight: {
      [operationBalloonFlightId: string]: number;
    } = {};
    flightData?.flightList?.forEach((flight) => {
      originalBusAllocs[flight.operationBalloonFlightId] =
        flight.allocatedPax || 0;

      originalBusAllocsWeight[flight.operationBalloonFlightId] =
        flight.totalWeight || 0;
    });
    setFlightAllocations(originalBusAllocs);
    setBusAllocationsWeight(originalBusAllocsWeight);

    // Restore original busPax count
    setRealTimeFlightPax({
      totalPax: flightData?.totalPax ?? 0,
      totalAllocatedPax: flightData?.totalAllocatedPax ?? 0,
    });

    // Reset selectedFlight to original state
    const initialSelectedBuses: { [key: string]: ISelectedFlightsData } = {};
    const initialOriginalAllocations: {
      [key: string]: IOriginalAllocation[];
    } = {};

    flightData?.bookings?.forEach((booking) => {
      const bookingIdString = String(booking.bookingId);
      const zoneIdStr = String(booking.zoneId);
      const bookingProductTripIdStr = String(booking.bookingProductTripId);
      const bookingIdStr = `${bookingIdString}_${zoneIdStr}_${bookingProductTripIdStr}`;

      if (!initialOriginalAllocations[bookingIdStr]) {
        initialOriginalAllocations[bookingIdStr] = [];
      }

      if (!initialSelectedBuses[bookingIdStr]) {
        initialSelectedBuses[bookingIdStr] = {
          bookingProductTripId: booking.bookingProductTripId,
          bookingId: booking.bookingId,
          paxNo: booking.paxNo,
          allocNo: booking.allocNo,
          zoneId: booking?.zoneId,
          allocations: [],
        };
      }

      booking.flightAllocation?.forEach((allocation) => {
        if (allocation.operationBalloonFlightId) {
          initialOriginalAllocations[bookingIdStr].push({
            operationBalloonFlightId: allocation.operationBalloonFlightId,
            paxNo: Number(allocation.paxNo || booking.paxNo),
          });

          initialSelectedBuses[bookingIdStr].allocations.push({
            operationBalloonFlightId: allocation.operationBalloonFlightId,
            splitMaleNo: Number(allocation?.male),
            splitFemaleNo: Number(allocation?.female),
            splitChildNo: Number(allocation?.child),
            oldOperationBalloonFlightId: allocation.operationBalloonFlightId,
            splitPax: Number(allocation.paxNo || booking.paxNo),
            isDeleted: false,
            maleWeight: booking?.maleWeight,
            femaleWeight: booking?.femaleWeight,
            childWeight: booking?.childWeight,
          });
        }
      });

      if (initialSelectedBuses[bookingIdStr].allocations.length === 0) {
        delete initialSelectedBuses[bookingIdStr];
      }
    });

    setOriginalAllocations(initialOriginalAllocations);
    setSelectedFlight(initialSelectedBuses);
    setIsEditMode(false);
  };

  // Handle edit button click
  const toggleColumn = (column: keyof IVisibleColumns) => {
    setVisibleColumns((prev) => ({
      ...prev,
      [column]: !prev[column],
    }));
  };

  // this fun use to handle pax count
  const handlePaxCountChange = (
    bookingId: string | number,
    bookingProductTripId: number | string,
    operationBalloonFlightId: number,
    field: 'splitMaleNo' | 'splitFemaleNo' | 'splitChildNo',
    value: number | '',
    zoneId: number,
  ) => {
    const bookingIdString = String(bookingId);
    const operationBalloonFlightIdStr = String(operationBalloonFlightId);
    // const key = `${bookingIdStr}_${operationBalloonFlightIdStr}_${field}`;
    const zoneIdStr = String(zoneId);
    const bookingProductTripIdStr = String(bookingProductTripId);
    const combinedKey = `${bookingIdString}_${zoneIdStr}_${bookingProductTripIdStr}`;

    setSelectedFlight((prev) => {
      const currentBooking = prev[combinedKey];
      if (!currentBooking) return prev;

      // Create a deep copy of allocations
      const updatedAllocations = currentBooking.allocations.map(
        (allocation) => ({ ...allocation }),
      );

      // Find the current allocation index
      const currentAllocationIndex = updatedAllocations.findIndex(
        (a) =>
          String(a.operationBalloonFlightId) === operationBalloonFlightIdStr &&
          !a.isDeleted,
      );

      if (currentAllocationIndex === -1) return prev;

      // Get the current allocation
      const currentAllocation = updatedAllocations[currentAllocationIndex];

      // If input is cleared, store an empty string instead of 0
      const newValue = value === '' ? '' : Number(value);

      // Update the allocation with the new value for the specific field
      const updatedAllocation = { ...currentAllocation, [field]: newValue };

      // Calculate new splitPax as sum of male, female, and child counts
      const maleCount = Number(updatedAllocation.splitMaleNo || 0);
      const femaleCount = Number(updatedAllocation.splitFemaleNo || 0);
      const childCount = Number(updatedAllocation.splitChildNo || 0);
      const newSplitPax = maleCount + femaleCount + childCount;

      const updatedAllocation_SplitPax = {
        ...updatedAllocation,
        splitPax: newSplitPax,
      };

      // Update the allocations list
      updatedAllocations[currentAllocationIndex] = updatedAllocation_SplitPax;

      // Calculate total allocated pax across all allocations
      const totalAllocatedPax = updatedAllocations.reduce((sum, a) => {
        if (a.isDeleted) return sum;
        const allocMaleCount = Number(a.splitMaleNo || 0);
        const allocFemaleCount = Number(a.splitFemaleNo || 0);
        const allocChildCount = Number(a.splitChildNo || 0);
        return sum + allocMaleCount + allocFemaleCount + allocChildCount;
      }, 0);

      const totalPax = Number(currentBooking.paxNo);

      return {
        ...prev,
        [combinedKey]: {
          ...currentBooking,
          allocations: updatedAllocations,
          allocNo: totalAllocatedPax,
        },
      } as { [key: string]: ISelectedFlightsData };
    });
  };
  
  const encodedDate = useMemo(
    () => encodeDateForUrl(selectedDate),
    [selectedDate],
  );
  const {
    data: operationDetailsData,
    isLoading: tempLoading,
    isFetching,

    refetch,
  } = useOperationDetails(selectedLocation.OwnerId, encodedDate);

  useEffect(() => {
    if (operationDetailsData) {
      setApiResponse(operationDetailsData); // ✅ update state here
    }
  }, [operationDetailsData]);
  const { selectedTemperature, setSelectedTemperature } = useTemperatureStore();
  const [temp, setTemp] = useState<number>();
  const [apiResponse, setApiResponse] = useState<IOperations>();
  const addTemperature = async (data: IAddTemperatureRequest) => {
    try {
      const response = await operationAPI.addTemperature(data);
      showOrReplaceToast(
        'operations',
        translate('operation.tempAddedSuccessfully'),
        'success',
      );
      console.log(response, 'this is the value of the response');
    } catch (error) {
      showOrReplaceToast(
        'operations',
        translate('operation.commonError'),
        'error',
      );
    }
  };

  const [tempList, setTempList] = useState<ITemperature[]>([]);
  const [todayTemp, setTodayTemp] = useState<number>();
  const {
    data: temperatureList,
    isLoading: tempIsLoading,
    isFetching: tempIsFetching,
    isError,
    refetch: refetchTemp,
  } = useTemperatureList();

  useEffect(() => {
    if (temperatureList) {
      setTempList(temperatureList);
    }
  }, [temperatureList]);

  const {
    data: todayTemperature,
    isLoading: todayTempIsLoading,
    isFetching: todayTempIsFetching,
    refetch: refetchTodayTemp,
  } = useTodayTemperature(selectedLocation?.OwnerId, encodedDate);

  useEffect(() => {
    if (todayTemperature) {
      setTemp(todayTemperature.temperatureid1);
      setTodayTemp(todayTemperature.temperatureid1);
    }
  }, [todayTemperature]);

  useEffect(() => {
    refetchTodayTemp();
  }, [selectedLocation?.OwnerId, selectedDate]);

  return (
    <div className={'bg-background px-6'}>
      <div className=" bg-background">
        <CalendarHeader />
      </div>
      <div className="text-start flex justify-between">
        <div>
          <h1 className="text-lg font-semibold text-textColorGray">
            {translate('flightAllocation.flightAllocation')}
          </h1>
        </div>
        <div className="flex justify-between items-center bg-backgroundColor-tableBgColor  rounded p-3 mb-1">
          <span>
            <WarningIcon />
          </span>
          <span className="text-black ml-4 mr-6">
            {unAllocatedPaxPercentage}
            {translate('flightAllocation.percentageOfUnAllocated')}
          </span>

          <button
            disabled={Number(unAllocatedPaxPercentage) <= 0}
            onClick={() => onIsUnallocatedVisible()}
            className={`border pl-4 pr-4 pt-1 pb-1 rounded ${
              Number(unAllocatedPaxPercentage) > 0
                ? 'text-textColor border-borderColorBlue bg-backgroundColor-white cursor-pointer'
                : 'cursor-not-allowed bg-[var(--disableBtnBg)] text-[var(--disableBtnColor)] border--[var(--disableBtnBorder)]'
            }`}
          >
            {translate('common.view')}
          </button>
          <UnallocatedCard
            isVisible={isUnallocatedVisible}
            setIsVisible={setIsUnallocatedVisible}
            heading="Flight with Unallocated pax"
            data={unAllocatedPax ? unAllocatedPax : []}
          />
        </div>
      </div>
      {/* Filters */}
      <div className="bg-background mt-2 rounded-lg mb-2 flex items-end justify-between">
        <div className="flex">
          <div className="text-start mr-4 ">
            <label className="text-textColor">
              {translate('common.location')}
            </label>

            <ReusableSelectLocation<ILocation>
              options={(locationList || []) as ILocation[]}
              className="w-full border border-customBorder-border rounded p-2 text-sm  bg-sidebar-background text-text-primary"
              getOptionLabel={(option: ILocation) => `${option.locationName}`}
              getOptionValue={(option: ILocation) => option.OwnerId}
              onChange={handleLocationChange}
              placeholder="Select a locations"
              value={selectedLocationId}
            />
          </div>
          <div className="text-start mr-4 z-20">
            <label className="text-textColor">{translate('common.date')}</label>
            <div className="flex items-center h-[38px] p-2 rounded border border-customBorder-border bg-sidebar-background text-text-primary">
              <DateSelector
                onDateChange={handleDateChange}
                initialDate={selectedDate}
                showArrows={true}
              />
            </div>
          </div>
          <div className="text-start mr-4 ">
            <label className="text-text-primary">
              {translate('common.temp')}
            </label>

            <div
              className={`text-start  flex items-center h-[38px] rounded border border-customBorder-border  mr-2 bg-sidebar-background`}
            >
              <span>
                <div>
                  <div className="block dark:hidden">
                    <SunIcon />
                  </div>
                  <div className="hidden dark:block">
                    <SunIconDark />
                  </div>
                </div>
              </span>
              {todayTempIsLoading || todayTempIsFetching ? (
                <Loader />
              ) : (
                <ReusableSelect<ITemperature>
                  options={(tempList || []).sort(
                    (a, b) => a.temperature - b.temperature,
                  )}
                  className={`w-[80px] xl:w-full rounded p-2 text-text-primary  text-sm  bg-sidebar-background`}
                  getOptionLabel={(option: ITemperature) =>
                    `${option.temperature}°C`
                  }
                  getOptionValue={(option: ITemperature) =>
                    option.temperatureId
                  }
                  onChange={(selectedValue: ITemperature) => {
                    setTemp(selectedValue.temperatureId);
                    setSelectedTemperature(selectedValue); // Update Zustand store
                    const data = {
                      temp1: selectedValue.temperatureId,
                      ownerId: selectedLocation.OwnerId,
                      toDate: encodeDateForUrl(selectedDate),
                    };
                    addTemperature(data);
                  }}
                  value={temp || todayTemp || undefined}
                  placeholder="Temp"
                />
              )}
            </div>
          </div>
        </div>
        <div
          className={`w-[18%] 2xl:w-[12%] text-textColorGray text-sm font-medium flex items-center justify-between ${bgColor} h-[44px]`}
        >
          <span className="text-text-primary font-normal ml-4 text-[14px]">
            {translate('flightAllocation.flights')}
          </span>
          <span className="text-text-primary font-normal mr-4 text-[14px]">
            <span className="text-textColor">
              {/* {flightData?.totalAllocatedPax} */}
              {isEditMode
                ? realTimeFlightPax['totalAllocatedPax']
                : flightData?.totalAllocatedPax}
            </span>
            /{flightData?.totalPax}
          </span>
        </div>
      </div>

      {/* Cards */}
      <div className={`sticky top-0  grid grid-cols-6 gap-4 mt-4`}>
        {flightData?.flightList?.map(
          (flight: IFlightOperationDetails, index: number) => (
            <FlightCard
              key={index}
              flightName={flight?.formattedBalloonName}
              filledPercentage={`${Math.round((Number(flight.allocCount) / Number(flight.allocatedPax)) * 100)}`}
              currentCount={
                isEditMode
                  ? flightAllocations[flight?.operationBalloonFlightId]
                  : flight?.allocatedPax
              }
              totalCount={flight?.maxPax}
              totalWeight={`${flight?.balloonWeight}`}
              // filledWeight={`${flight.totalWeight}`}
              filledWeight={
                isEditMode
                  ? `${busAllocationsWeight[flight?.operationBalloonFlightId]}`
                  : `${flight?.totalWeight}`
              }
              pilotWeight={flight?.pilotWeight}
              bgColor={`${bgColor}`}
              cancelled={flight?.cancelled}
            />
          ),
        )}
      </div>

      {/* Table Controls */}
      <div
        className={`sticky z-10  mt-4 pt-2 ${flightData?.flightList?.length > 0 ? 'top-[133px]' : 'top-0'}`}
      >
        <div
          className={` flex justify-end items-center p-2 rounded-lg pb-2 bg-background`}
        >
          <div ref={ref} className="relative ">
            <ReusableButton
              variant="secondary"
              children={'Select Columns'}
              icon={
                <div>
                  <div className="block dark:hidden">
                    <AccordianDownIcon />
                  </div>
                  <div className="hidden dark:block">
                    <AccordianDownIconDark />
                  </div>
                </div>
              }
              textColor={'text-textColor'}
              onClick={onSetIsToggleColumns}
              bgColor={'bg-backgroundColor-darkThemeColor'}
            />
            {isToggleColumns && (
              <div className="absolute right-0 bg-backgroundColor-white border -mt-2 border-slate-100 shadow-lg rounded z-10 overflow-y-auto max-h-64">
                {(Object.keys(visibleColumns) as (keyof IVisibleColumns)[]).map(
                  (column) => {
                    // Find header for matching label and type
                    const header = headers.find(
                      (h) =>
                        h.label.replace(/\s+/g, '')?.toLowerCase() === column,
                    );
                    const isDynamicDriverColumn = header?.key;
                    const displayLabel = header ? header.label : column;

                    return (
                      <label
                        key={column}
                        className="flex items-center px-4 py-2 space-x-1 text-text-black"
                      >
                        <input
                          type="checkbox"
                          checked={visibleColumns[column]}
                          onChange={() => toggleColumn(column)}
                        />
                        <span
                          className={`text-text-secondary ${
                            isDynamicDriverColumn ? 'uppercase' : 'capitalize'
                          }`}
                        >
                          {displayLabel}
                        </span>
                      </label>
                    );
                  },
                )}
              </div>
            )}
          </div>
          <div className="flex space-x-2">
            {isEditMode ? (
              <>
                <ReusableIconButton
                  icon={
                    <CrossIcon
                      stroke={'#FF5733'}
                      className="w-6 h-6 text-red-500"
                    />
                  }
                  onClick={handleCancelEdit}
                  className="border-red-500"
                />

                <ReusableIconButton
                  icon={loading ? <Loader /> : <CheckIcon stroke={'green'} />}
                  onClick={() => allocatedFlight()}
                  className="border-green"
                />
              </>
            ) : (
              <ReusableButton
                variant="secondary"
                children={translate('common.edit')}
                icon={
                  <div>
                    <div className="block dark:hidden">
                      <EditIcon />
                    </div>
                    <div className="hidden dark:block">
                      <EditIconDark />
                    </div>
                  </div>
                }
                bgColor={'bg-backgroundColor-darkThemeColor'}
                textColor={'text-textColor'}
                onClick={toggleEditMode}
                isDisabled={flightData?.bookings?.length === 0}
              />
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className={`${bgColor} overflow-x-auto`}>
        <div className="max-h-[calc(100vh-200px)] overflow-y-auto">
          <table className="min-w-full border-collapse border border-customBorder-tableBorder shadow-lg text-left">
            <thead className="bg-[#004E9B] text-white sticky top-0">
              <tr className="text-sm">
                {headers
                  ?.filter((header) => {
                    const filterKey = header?.label
                      ?.replace(/\s+/g, '')
                      ?.toLowerCase();
                    return visibleColumns[filterKey] === true;
                  })
                  .map((header, index) => {
                    const flightHeaderToolTipColumn =
                      flightHeaderToolTipColumns.includes(header?.label);
                    return (
                      <th
                        key={index}
                        className="border border-slate-100 p-3 text-sm"
                        title={
                          flightHeaderToolTipColumn
                            ? flightHeaderToolTip[
                                header?.label.toLocaleLowerCase() as keyof typeof flightHeaderToolTip
                              ]
                            : undefined
                        }
                      >
                        <div className="flex flex-col">
                          <span>{header?.label}</span>
                          {header?.key && <span>{`M\\F\\Ch`}</span>}
                        </div>
                      </th>
                    );
                  })}
              </tr>
            </thead>
            <tbody>
              {isPageLoading ? (
                <tr>
                  <td
                    colSpan={headers?.length || 1}
                    className="text-center p-4 h-[200px] text-lg font-semibold"
                  >
                    <div className="inline-grid">
                      <Loader width={50} height={50} />
                    </div>
                  </td>
                </tr>
              ) : flightData?.bookings?.length === 0 ? (
                <tr>
                  <td
                    colSpan={headers?.length || 1}
                    className="text-center p-4 h-[200px] text-lg font-semibold text-text-primary"
                  >
                    {translate('flightAllocation.noData')}
                  </td>
                </tr>
              ) : (
                flightData?.bookings?.map((row, index) => {
                  const bookingIdString = String(row.bookingId);
                  const zoneIdStr = String(row?.zoneId);
                  const bookingProductTripIdStr = String(
                    row.bookingProductTripId,
                  );
                  const combinedKey = `${bookingIdString}_${zoneIdStr}_${bookingProductTripIdStr}`;
                  const highlight =
                    selectedFlight[combinedKey] &&
                    selectedFlight[combinedKey]?.allocations?.filter(
                      (a) => !a.isDeleted,
                    )?.length > 0;
                  return (
                    <tr
                      key={index}
                      className={`${!highlight ? 'bg-backgroundColor-tableBgColor text-black ' : index % 2 === 0 ? 'bg-backgroundColor-white text-text-primary' : 'bg-gray-100 text-text-primary'} `}
                    >
                      {headers
                        ?.filter((header) => {
                          const filterKey = header?.label
                            ?.replace(/\s+/g, '')
                            ?.toLowerCase();
                          return visibleColumns[filterKey] === true;
                        })?.map((header, headerIndex) => {
                          const filterKey = header?.label
                            ?.replace(/\s+/g, '')
                            ?.toLowerCase();

                          const rowKey = keyMapping[filterKey];

                          const flight = flightData?.flightList?.find(
                            (f) =>
                              f?.balloonName
                                .replace(/\s+/g, '')
                                ?.toLowerCase() ===
                              header.label.replace(/\s+/g, '')?.toLowerCase(),
                          );
                          if (flight) {
                            const bookingIdStr = String(row.bookingId);
                            const zoneIdStr = String(row.zoneId);
                            const bookingProductTripIdStr = String(
                              row.bookingProductTripId,
                            );
                            const combinedKey = `${bookingIdStr}_${zoneIdStr}_${bookingProductTripIdStr}`;
                            return (
                              <td
                                key={headerIndex}
                                className="border-gray-300 p-3 text-center"
                              >
                                <input
                                  type="checkbox"
                                  disabled={!isEditMode}
                                  className="w-5 h-5 text-textColor border-gray-300 rounded focus:ring-blue-500"
                                  checked={
                                    // First check if this bus is selected in our state
                                    selectedFlight[
                                      combinedKey
                                    ]?.allocations.some(
                                      (a) =>
                                        String(a.operationBalloonFlightId) ===
                                          String(
                                            flight.operationBalloonFlightId,
                                          ) && !a.isDeleted,
                                    ) ||
                                    // Or if it's in the original allocation but not yet in our state
                                    (!selectedFlight[combinedKey] &&
                                      row.flightAllocation?.some(
                                        (allocation) =>
                                          String(
                                            allocation.operationBalloonFlightId,
                                          ) ===
                                          String(
                                            flight.operationBalloonFlightId,
                                          ),
                                      ))
                                  }
                                  onChange={() => {
                                    // Find the existing allocation if any
                                    const existingAllocation =
                                      row.flightAllocation?.find(
                                        (allocation) =>
                                          String(
                                            allocation.operationBalloonFlightId,
                                          ) ===
                                          String(
                                            flight.operationBalloonFlightId,
                                          ),
                                      );

                                    handleCheckboxChange(
                                      row?.bookingId,
                                      flight?.operationBalloonFlightId,
                                      row?.bookingProductTripId,
                                      row?.paxNo,
                                      row?.male,
                                      row?.female,
                                      row?.children,
                                      row?.maleWeight,
                                      row?.femaleWeight,
                                      row?.childWeight,
                                      row?.zoneId,
                                      existingAllocation?.operationBalloonFlightId ||
                                        null,
                                    );
                                  }}
                                />

                                {isEditMode &&
                                  selectedFlight[
                                    combinedKey
                                  ]?.allocations?.filter((a) => !a.isDeleted)
                                    .length > 1 &&
                                  selectedFlight[
                                    combinedKey
                                  ]?.allocations?.some(
                                    (a) =>
                                      String(a.operationBalloonFlightId) ===
                                        String(
                                          flight.operationBalloonFlightId,
                                        ) && !a.isDeleted,
                                  ) && (
                                    <div className="flex">
                                      {/* Male input */}
                                      <div className="text-left">
                                        <input
                                          type="number"
                                          placeholder="M"
                                          className={`w-16 p-1 border rounded mr-[1px] text-black`}
                                          value={
                                            selectedFlight[
                                              combinedKey
                                            ]?.allocations.find(
                                              (allocation) =>
                                                String(
                                                  allocation.operationBalloonFlightId,
                                                ) ===
                                                  String(
                                                    flight?.operationBalloonFlightId,
                                                  ) && !allocation.isDeleted,
                                            )?.splitMaleNo ?? ''
                                          }
                                          onChange={(e) => {
                                            const inputValue =
                                              e.target.value === ''
                                                ? ''
                                                : Number(e.target.value);

                                            handlePaxCountChange(
                                              row.bookingId,
                                              row.bookingProductTripId,
                                              flight?.operationBalloonFlightId,
                                              'splitMaleNo',
                                              inputValue,
                                              row?.zoneId,
                                            );
                                          }}
                                        />
                                      </div>

                                      {/* Female input */}
                                      <div className="text-left">
                                        <input
                                          type="number"
                                          placeholder="F"
                                          className={`w-16 p-1 border rounded mr-[1px] text-black`}
                                          value={
                                            selectedFlight[
                                              combinedKey
                                            ]?.allocations.find(
                                              (allocation) =>
                                                String(
                                                  allocation.operationBalloonFlightId,
                                                ) ===
                                                  String(
                                                    flight?.operationBalloonFlightId,
                                                  ) && !allocation.isDeleted,
                                            )?.splitFemaleNo ?? ''
                                          }
                                          onChange={(e) => {
                                            const inputValue =
                                              e.target.value === ''
                                                ? ''
                                                : Number(e.target.value);

                                            handlePaxCountChange(
                                              row.bookingId,
                                              row.bookingProductTripId,
                                              flight?.operationBalloonFlightId,
                                              'splitFemaleNo',
                                              inputValue,
                                              row.zoneId,
                                            );
                                          }}
                                        />
                                      </div>
                                      {/* Child input */}
                                      <div className="text-left">
                                        <input
                                          type="number"
                                          placeholder="Ch"
                                          className={`w-16 p-1 border rounded mr-[1px] text-black`}
                                          value={
                                            selectedFlight[
                                              combinedKey
                                            ]?.allocations.find(
                                              (allocation) =>
                                                String(
                                                  allocation.operationBalloonFlightId,
                                                ) ===
                                                  String(
                                                    flight?.operationBalloonFlightId,
                                                  ) && !allocation.isDeleted,
                                            )?.splitChildNo ?? ''
                                          }
                                          onChange={(e) => {
                                            const inputValue =
                                              e.target.value === ''
                                                ? ''
                                                : Number(e.target.value);

                                            handlePaxCountChange(
                                              row.bookingId,
                                              row.bookingProductTripId,
                                              flight?.operationBalloonFlightId,
                                              'splitChildNo',
                                              inputValue,
                                              row?.zoneId,
                                            );
                                          }}
                                        />
                                      </div>
                                    </div>
                                  )}
                              </td>
                            );
                          }

                          if (!visibleColumns[filterKey] || !rowKey)
                            return null;
                          const rowData = row?.[rowKey as keyof typeof row];

                          if (filterKey === allocation.ALLOCATED) {
                            const bookingIdStr = String(row.bookingId);
                            const zoneIdStr = String(row.zoneId);
                            const bookingProductTripIdStr = String(
                              row.bookingProductTripId,
                            );
                            const combinedKey = `${bookingIdStr}_${zoneIdStr}_${bookingProductTripIdStr}`;
                            const paxAllocatedCount =
                              selectedFlight[combinedKey] &&
                              selectedFlight[combinedKey]?.allocations?.filter(
                                (a) => !a.isDeleted,
                              )?.length > 0
                                ? selectedFlight[combinedKey]?.allocNo
                                : 0;

                            return (
                              <td
                                key={headerIndex}
                                className="border-gray-300 p-3 min-w-8"
                              >
                                {paxAllocatedCount}
                              </td>
                            );
                          }

                          const shouldTruncate =
                            truncateColumn.includes(filterKey);
                          const eRHighlight =
                            filterKey === allocation.EARLYRETURN &&
                            rowData === true;

                          const displayValue =
                            filterKey === allocation.LUGGAGE
                              ? rowData === true
                                ? translate('common.yes')
                                : rowData === false
                                  ? translate('common.no')
                                  : rowData
                              : filterKey === allocation.EARLYRETURN
                                ? rowData === true
                                : translate('common.Y')
                                  ? rowData === false
                                    ? ''
                                    : rowData
                                  : rowData;
                          return (
                            <td
                              key={headerIndex}
                              className={`border-gray-300 p-3 ${
                                shouldTruncate
                                  ? 'truncate max-w-xs cursor-pointer'
                                  : ''
                              } ${eRHighlight ? 'text-red-600' : ''}`}
                              title={
                                shouldTruncate &&
                                typeof displayValue === 'string'
                                  ? displayValue
                                  : undefined
                              }
                            >
                              {shouldTruncate &&
                              typeof displayValue === 'string' &&
                              displayValue.length > 20
                                ? `${displayValue.substring(0, 20)}...`
                                : Array.isArray(displayValue)
                                  ? JSON.stringify(displayValue) // Convert array to string for rendering
                                  : displayValue}
                            </td>
                          );
                        })}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default FlightDesktop;
